// --- src/components/SearchBar.js ---
import React, { useMemo, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { postTypes, locationData } from '../utils/constants';
import Select from 'react-select';
import './SearchBar.css';

// Compact Styles for React-Select
const customSelectStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: '44px', 
        height: '46px', /* Larger size for easy finger taps on mobile screens */
        border: '1px solid rgba(255, 255, 255, 0.25)',
        borderRadius: '50px',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
        fontSize: '15px', /* Enlarged font size */
        fontWeight: '600',
        minWidth: '175px', 
        backgroundColor: 'rgba(255, 255, 255, 0.12)', /* Clean glass translucent pills */
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            borderColor: '#ff9800',
            backgroundColor: 'rgba(255, 255, 255, 0.18)'
        }
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: '0 16px',
        height: '44px',
        display: 'flex',
        alignItems: 'center'
    }),
    singleValue: (provided) => ({
        ...provided,
        color: '#ffffff' /* Crisp white font readability over dark background */
    }),
    placeholder: (provided) => ({
        ...provided,
        color: 'rgba(255, 255, 255, 0.7)' /* High contrast placeholder */
    }),
    input: (provided) => ({ 
        ...provided, 
        margin: '0', 
        padding: '0',
        color: '#ffffff'
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (provided) => ({ 
        ...provided, 
        padding: '4px', 
        color: '#ff9800',
        backgroundColor: 'transparent !important',
        border: 'none !important'
    }),
    clearIndicator: (provided) => ({
        ...provided,
        padding: '4px',
        color: 'rgba(255, 255, 255, 0.6)',
        backgroundColor: 'transparent !important',
        border: 'none !important'
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        backgroundColor: 'transparent !important',
        border: 'none !important'
    }),
    menu: (provided) => ({ 
        ...provided, 
        zIndex: 9999, 
        minWidth: '190px', 
        borderRadius: '12px', 
        border: '1px solid rgba(255, 255, 255, 0.15)',
        backgroundColor: '#1a1a1a', /* Elegant matching solid dark dropdown overlay menu */
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.35)'
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected 
            ? '#ff9800' 
            : state.isFocused 
                ? 'rgba(255, 255, 255, 0.1)' 
                : 'transparent',
        color: '#ffffff',
        cursor: 'pointer',
        padding: '10px 16px',
        fontSize: '14px',
        '&:active': {
            backgroundColor: '#e68a00'
        }
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }) 
};

// HELPER HOOK FOR OPTIONS
const useDropdownOptions = (filters) => {
    const stateOptions = React.useMemo(() => locationData.map(s => ({ value: s.state_en, label: s.state_te })), []);
    
    const districtOptions = React.useMemo(() => {
        if (!filters.state) return [];
        const stateData = locationData.find(s => s.state_en === filters.state);
        return stateData ? stateData.districts.map(d => ({ value: d.district_en, label: d.district_te })) : [];
    }, [filters.state]);

    const constituencyOptions = React.useMemo(() => {
        if (!filters.state || !filters.district) return [];
        const stateData = locationData.find(s => s.state_en === filters.state);
        const districtData = stateData?.districts.find(d => d.district_en === filters.district);
        return districtData?.constituencies || [];
    }, [filters.state, filters.district]);

    const mandalOptions = React.useMemo(() => {
        if (!filters.state || !filters.district) return []; 
        const stateData = locationData.find(s => s.state_en === filters.state);
        const districtData = stateData?.districts.find(d => d.district_en === filters.district);
        return districtData?.mandals || [];
    }, [filters.state, filters.district]);

    return { stateOptions, districtOptions, constituencyOptions, mandalOptions };
};

const postTypeOptions = [
    { value: '', label: 'అన్ని వార్తలు' },
    { value: 'Following', label: 'నేను ఫాలో అయ్యే వారి పోస్టులు' }, // Posts from accounts you follow
    { value: 'Followers', label: 'నా ఫాలోవర్స్ పోస్టులు' },       // Posts from accounts following you
    { value: 'World', label: 'ప్రపంచం' },
    { value: 'India', label: 'భారతదేశం' },
    { value: 'Andhra Pradesh', label: 'ఆంధ్రప్రదేశ్' },
    { value: 'Telangana', label: 'తెలంగాణ' },
];

const SearchBar = ({ filters, setFilters, onSearch }) => {
    const navigate = useNavigate();
    const { stateOptions, districtOptions, constituencyOptions, mandalOptions } = useDropdownOptions(filters);

    // 💡 NEW: Ref to capture the horizontal scroll container
    const scrollContainerRef = useRef(null);

    // 💡 NEW: Smoothly scroll horizontal menu to show the next level selections
    useEffect(() => {
        if (!scrollContainerRef.current) return;

        // Scroll to reveal constituency once a district is selected
        if (filters.district && !filters.constituency) {
            scrollContainerRef.current.scrollTo({
                left: 185, 
                behavior: 'smooth'
            });
        }
        // Scroll to reveal mandal once a constituency is selected
        else if (filters.constituency) {
            scrollContainerRef.current.scrollTo({
                left: 365, 
                behavior: 'smooth'
            });
        }
        // Scroll back to start on filter reset
        else if (!filters.district) {
            scrollContainerRef.current.scrollTo({
                left: 0,
                behavior: 'smooth'
            });
        }
    }, [filters.district, filters.constituency]);

    const handlePostTypeChange = (selected) => {
        const newPostType = selected ? selected.value : '';
        const newStateValue = (newPostType === 'Andhra Pradesh' || newPostType === 'Telangana') ? newPostType : '';

        const newFilters = { 
            ...filters,
            postType: newPostType, 
            state: newStateValue, 
            district: '',
            constituency: '',
            mandal: ''
        };
        setFilters(newFilters);
    };

    const viewAllUrl = `/posts/${encodeURIComponent(filters.postType || 'All')}?state=${encodeURIComponent(filters.state)}&district=${encodeURIComponent(filters.district)}&constituency=${encodeURIComponent(filters.constituency)}&mandal=${encodeURIComponent(filters.mandal)}`;

    return (
        <div className="search-bar-container">
            {/* 💡 FIXED: ref assigned to the scroll list */}
            <div className="filter-scroll-container" ref={scrollContainerRef}>
                <div className="filter-item">
                    <Select
                        className="search-select post-type-select"
                        options={postTypeOptions}
                        value={postTypeOptions.find(opt => opt.value === filters.postType) || postTypeOptions[0]}
                        onChange={handlePostTypeChange}
                        placeholder="అన్ని పోస్టులు" 
                        isSearchable={false}
                        styles={customSelectStyles}
                        menuPortalTarget={document.body} 
                    />
                </div>
                
                {(filters.postType === 'Andhra Pradesh' || filters.postType === 'Telangana') && (
                    <>
                        <div className="filter-item">
                            <Select
                                className="search-select" 
                                options={districtOptions}
                                value={filters.district ? {value: filters.district, label: districtOptions.find(d=>d.value===filters.district)?.label} : null}
                                onChange={selected => {
                                    setFilters({ ...filters, district: selected ? selected.value : '', constituency: '', mandal: '' });
                                }}
                                placeholder="జిల్లా" isClearable
                                styles={customSelectStyles}
                                menuPortalTarget={document.body} 
                            />
                        </div>

                        {filters.district && (
                            <div className="filter-item">
                                <Select
                                    className="search-select" 
                                    options={constituencyOptions}
                                    value={constituencyOptions.find(c => c.value === filters.constituency) || null}
                                    onChange={selected => {
                                        setFilters({ ...filters, constituency: selected ? selected.value : '', mandal: '' });
                                    }}
                                    placeholder="నియోజకవర్గం" isClearable 
                                    styles={customSelectStyles}
                                    menuPortalTarget={document.body} 
                                />
                            </div>
                        )}

                        {filters.constituency && (
                            <div className="filter-item">
                                <Select
                                    className="search-select" 
                                    options={mandalOptions}
                                    value={mandalOptions && filters.mandal ? mandalOptions.find(m => m.value === filters.mandal) : null}
                                    onChange={selected => {
                                        setFilters({ ...filters, mandal: selected ? selected.value : '' });
                                    }}
                                    placeholder="మండలం" isClearable
                                    styles={customSelectStyles}
                                    menuPortalTarget={document.body} 
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
            {/* Colorful Category Grid Logo Link */}
            <Link to={viewAllUrl} className="category-logo-link" title="విభాగాలు">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="colorful-category-icon">
                    <rect x="3" y="3" width="7" height="7" rx="2.2" fill="url(#grad-red)" />
                    <rect x="14" y="3" width="7" height="7" rx="2.2" fill="url(#grad-orange)" />
                    <rect x="3" y="14" width="7" height="7" rx="2.2" fill="url(#grad-blue)" />
                    <rect x="14" y="14" width="7" height="7" rx="2.2" fill="url(#grad-green)" />
                    <defs>
                        <linearGradient id="grad-red" x1="3" y1="3" x2="10" y2="10" gradientUnits="userSpaceOnUse">
                            <stop offset="0%" stopColor="#FF5E62" />
                            <stop offset="100%" stopColor="#FF2D55" />
                        </linearGradient>
                        <linearGradient id="grad-orange" x1="14" y1="3" x2="21" y2="10" gradientUnits="userSpaceOnUse">
                            <stop offset="0%" stopColor="#FFD97D" />
                            <stop offset="100%" stopColor="#FF9500" />
                        </linearGradient>
                        <linearGradient id="grad-blue" x1="3" y1="14" x2="10" y2="21" gradientUnits="userSpaceOnUse">
                            <stop offset="0%" stopColor="#00C6FF" />
                            <stop offset="100%" stopColor="#0072FF" />
                        </linearGradient>
                        <linearGradient id="grad-green" x1="14" y1="14" x2="21" y2="21" gradientUnits="userSpaceOnUse">
                            <stop offset="0%" stopColor="#11998e" />
                            <stop offset="100%" stopColor="#38ef7d" />
                        </linearGradient>
                    </defs>
                </svg>
            </Link>
        </div>
    );
};

export default SearchBar;